import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import "./i18n";
import { configureNativeUI } from "@/native";
import { installClientErrorReporter } from "@/utils/clientErrorReporter";

// Install the client-side error reporter BEFORE the React tree mounts so we
// catch any error thrown during initial render (rare but high-value bugs).
// Reports go to /api/client-errors and are auto-pruned server-side after 30
// days. See backend/routes/client_errors.py for the storage / rate-limiting.
installClientErrorReporter();

// Strip non-essential console output in production builds (137 call sites
// flagged by the code review). console.error and console.warn are always
// preserved so genuine failures and our own diagnostic warnings still surface
// in browser DevTools and the client-error reporter. console.log / .info /
// .debug / .trace become no-ops outside development to remove the perf
// overhead and avoid leaking internal state to end-users.
if (process.env.NODE_ENV === 'production') {
  const noop = () => {};
  // eslint-disable-next-line no-console
  console.log = noop;
  // eslint-disable-next-line no-console
  console.info = noop;
  // eslint-disable-next-line no-console
  console.debug = noop;
  // eslint-disable-next-line no-console
  console.trace = noop;
}

// On iOS (Capacitor), hide the splash + style the status bar to match our
// dark theme. No-op on web — safe to call unconditionally.
configureNativeUI();

// Offline app shell (Jun 2026): lets the app open with zero connectivity so
// queued clock punches can still be captured on job sites. /api is never
// intercepted; static assets are hash-named so caching is safe.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Stale-build self-healing (Aug 2026): phones keep tabs/PWAs alive for weeks,
// so after a deploy a device can keep running an old bundle (symptom: blank
// dialogs). When the user returns to the app, compare the served bundle with
// the running one and reload once if they differ. Checks only on tab-refocus
// so an active user is never interrupted mid-form.
if (process.env.NODE_ENV === 'production') {
  let reloading = false;
  const runningScript = Array.from(document.scripts)
    .map((s) => s.src)
    .find((s) => s.includes('/static/js/main.'));
  const checkBuild = async () => {
    if (reloading || !runningScript || document.visibilityState !== 'visible') return;
    try {
      const res = await fetch('/', { cache: 'no-store' });
      const html = await res.text();
      const served = html.match(/static\/js\/main\.[^"']+\.js/);
      if (served && !runningScript.includes(served[0])) {
        reloading = true;
        if (navigator.serviceWorker?.getRegistration) {
          try { (await navigator.serviceWorker.getRegistration())?.update(); } catch { /* ignore */ }
        }
        window.location.reload();
      }
    } catch { /* offline — ignore */ }
  };
  document.addEventListener('visibilitychange', checkBuild);
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
